import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getCreatedToastMessage, getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type { HookParams, Store, StoreData, StoreListParams, Stores } from '@/libs/interfaces'
import { storesApi } from '@/services/api'

// Unlike useStores (paginated /stores listing shape), this hits the same
// /orgs/:orgID/stores route but returns the flat Store[] the auth flow and
// the topbar store switcher need — see useAuth's currentStore-sync effect.
export function useOrgStores(orgID: string, enabled = true) {
  return useQuery<Store[]>({
    queryKey: QUERY_KEYS.stores.orgList(orgID),
    queryFn: () => storesApi.getStores(orgID),
    enabled: enabled && !!orgID,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useStores({ params, enabled }: HookParams<StoreListParams>) {
  return useQuery<Partial<Stores[]>>({
    queryKey: QUERY_KEYS.stores.list(params),
    queryFn: () => storesApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['24Hours'], //24 hours
  })
}

export function useStoresByID({
  params,
  enabled,
  skipErrorRedirect,
}: HookParams<Stores> & { skipErrorRedirect?: boolean }) {
  return useQuery<StoreData>({
    queryKey: QUERY_KEYS.stores.list(params),
    queryFn: () => storesApi.byID(params, skipErrorRedirect),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['24Hours'], //24 hours
    meta: { skipErrorRedirect },
  })
}
export function useInfiniteStores({ params, enabled }: HookParams<StoreListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.stores.infinite(params),
    queryFn: ({ pageParam }) =>
      storesApi.listCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'], //24 Hours
  })
}

export function useStoresCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: storesApi.create,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.stores.all,
      })
      toast.success(getCreatedToastMessage(result, t('settings.storeCreated')))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useStoresDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: storesApi.delete,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: QUERY_KEYS.stores.all })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useStoreUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: storesApi.update,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: QUERY_KEYS.stores.all })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}
